import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  Typography,
  IconButton,
  Divider,
  Tabs,
  Tab,
  FormControl,
  MenuItem,
  InputLabel,
  Select,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useState } from 'react';
import { ProfileDto } from "../../types/UserToken";
import { useAuth } from "../../context/AuthContext";

interface CalendarEvent {
  id: string;
  title: string;
  start: string;
  description?: string;
  origin: 'internal' | 'google';
  status?: string;
  property?: string;
  agent?: string;
  notes?: string;
}

interface DrawerSidebarProps {
  open: boolean;
  onToggle: () => void;
  events: CalendarEvent[];
  onSelectEvent: (event: CalendarEvent) => void;
}

type AppointmentStatus = 'pending' | 'scheduled' | 'cancelled';

const getStatusColor = (status: string):
  | 'success'
  | 'error'
  | 'warning'
  | 'info'
  | 'primary'
  | 'secondary' => {
  switch (status.toLowerCase()) {
    case 'pending':
      return 'warning';
    case 'scheduled':
      return 'success';
    case 'cancelled':
      return 'error';
    default:
      return 'info';
  }
};

const spanishStatus = (status: string | undefined): string => {
  switch (status?.toLowerCase()) {
    case 'scheduled':
      return 'Programada';
    case 'confirmed':
      return 'Confirmada';
    case 'cancelled':
      return 'Cancelada';
    case 'pending':
      return 'Pendiente';
    default:
      return status || '';
  }
};

const DrawerSidebar: React.FC<DrawerSidebarProps> = ({ open, onToggle, events, onSelectEvent }) => {
  const [filterStatus, setFilterStatus] = useState<'all' | 'pending' | 'scheduled' | 'cancelled'>('all');
  const [activeTab, setActiveTab] = useState(0);
  const { userToken } = useAuth();
  const [searchTerm] = useState('');

  const currentUser: ProfileDto = {
    id: userToken?.user?.profiles[0]?.id ?? 0,
    username: userToken?.user?.profiles[0]?.username ?? "",
    userProfileId: 0
  };

  const filteredEvents = events
    .filter((event) => {
      return filterStatus === 'all' || event.status?.toLowerCase() === filterStatus;
    })
    .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());

  const groupedEvents = filteredEvents.reduce((groups: any, event) => {
    const dateStr = new Date(event.start).toDateString();
    if (!groups[dateStr]) groups[dateStr] = [];
    groups[dateStr].push(event);
    return groups;
  }, {});

  const agendaToday = events
    .filter((event) => new Date(event.start).toDateString() === new Date().toDateString())
    .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());

    const nextAppointments = events
    .filter((event) => new Date(event.start) > new Date())
    .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());

  return (
    <Drawer
      variant="persistent"
      anchor="left"
      open={open}
      sx={{
        width: { xs: '100%', sm: 320 },
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 320 },
          top: { xs: 64, sm: '10vh' }, // header height en mobile vs desktop
          height: { xs: 'calc(100% - 64px)', sm: '90vh' },
          position: 'fixed',
          backgroundColor: '#fafafa',
          p: 2,
          overflowY: 'auto',
          zIndex: 1200,
        },
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        flexWrap="wrap"
      >
        <Typography variant="h6"
          sx={{
            fontSize: { xs: '1rem', sm: '1.25rem' },
            mb: { xs: 1, sm: 0 },
          }}
        >¡Bienvenido {currentUser.username}!</Typography>
        <IconButton onClick={onToggle} size="small"
          sx={{
            color: 'text.primary',
            p: { xs: 1.5, sm: 1 },           // más padding en móvil
            '& svg': {
              fontSize: { xs: '1.75rem', sm: '1.25rem' }, // agrandar ícono en xs
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Tabs
        value={activeTab}
        onChange={(e, newValue) => setActiveTab(newValue)}
        variant="fullWidth"
        sx={{ mb: 2 }}
      >
        <Tab label="Agenda de Hoy" />
        <Tab label="Eventos" />
        <Tab label="Próximas" />
      </Tabs>

      {/* Tab 0: Agenda de Hoy */}
      {activeTab === 0 && (
        <Box>
          {agendaToday.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              No hay citas programadas para hoy 💤
            </Typography>
          ) : (
            <List disablePadding>
              {agendaToday.map((event) => (
                <ListItem key={event.id} disablePadding divider>
                  <ListItemButton sx={{ flexWrap: 'wrap' }} onClick={() => onSelectEvent(event)}>
                    <ListItemText
                      primary={event.title}
                      secondary={`Hora: ${new Date(event.start).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}`}
                    />
                    <Chip
                      size="small"
                      label={spanishStatus(event.status)}
                      color={getStatusColor(event.status ?? '')}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      )}

      {/* Tab 1: Por Estado */}
      {activeTab === 1 && (
        <Box >
          <Typography variant="subtitle1" mb={1}>
            Total de eventos: {filteredEvents.length}
          </Typography>

          {/* ✅ Filtro por estado */}
          <Box display="flex" justifyContent="center" width="95%">
            <FormControl fullWidth
              size="small"
              sx={{
                mb: 2,
                minWidth: { xs: '100%', sm: 200 },
              }}
            >
              <InputLabel id="filter-label">Filtrar por estado</InputLabel>
              <Select
                labelId="filter-label"
                value={filterStatus}
                label="Filtrar por estado"
                onChange={(e) => setFilterStatus(e.target.value as AppointmentStatus | 'all')}
              >
                <MenuItem value="all">Todas</MenuItem>
                <MenuItem value="pending">Pendientes</MenuItem>
                <MenuItem value="scheduled">Confirmadas</MenuItem>
                <MenuItem value="cancelled">Canceladas</MenuItem>
              </Select>
            </FormControl>
          </Box>


          {/* 🗓 Lista agrupada por fecha */}
          <List>
            {Object.entries(groupedEvents).map(([dateStr, dayEvents]) => {
              const filteredBySearch = (dayEvents as CalendarEvent[]).filter((event) =>
                (event.title + event.agent + event.property)
                  .toLowerCase()
                  .includes(searchTerm.toLowerCase())
              );

              if (filteredBySearch.length === 0) return null;

              return (
                <Box key={dateStr} mb={2}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 1 }}>
                    {new Date(dateStr).toLocaleDateString(undefined, {
                      weekday: 'long',
                      day: 'numeric',
                      month: 'short',
                    })}
                  </Typography>
                  <Divider />
                  <List disablePadding>
                    {filteredBySearch.map((event) => (
                      <ListItem key={event.id} disablePadding divider>
                        <ListItemButton onClick={() => onSelectEvent(event)}>
                          <ListItemText
                            primary={
                              <Typography
                                sx={{
                                  textDecoration:
                                    event.status?.toLowerCase() === 'confirmed' ? 'line-through' : 'none',
                                  color:
                                    event.status?.toLowerCase() === 'confirmed' ? 'text.disabled' : 'inherit',
                                }}
                              >
                                {event.property || event.title}
                              </Typography>
                            }
                            secondary={
                              <Typography
                                sx={{
                                  textDecoration:
                                    event.status?.toLowerCase() === 'confirmed' ? 'line-through' : 'none',
                                  fontSize: '0.85rem',
                                  color:
                                    event.status?.toLowerCase() === 'confirmed' ? 'text.disabled' : 'text.secondary',
                                }}
                              >
                                Hora: {new Date(event.start).toLocaleTimeString([], {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })} • Agente: {event.agent ?? 'N/A'}
                              </Typography>
                            }
                          />
                          {event.status && (
                            <Chip
                              size="small"
                              label={spanishStatus(event.status)}
                              color={getStatusColor(event.status)}
                            />
                          )}
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              );
            })}
          </List>
        </Box>
      )}

      {/* Tab 2: Sorpresa Pro */}
      {activeTab === 2 && (
        <Box>
          {nextAppointments.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              No tienes próximas citas programadas.
            </Typography>
          ) : (
            <List disablePadding>
              {nextAppointments.map((event) => (
                <ListItem key={event.id} disablePadding divider>
                  <ListItemButton sx={{ flexWrap: 'wrap' }} onClick={() => onSelectEvent(event)}>
                    <ListItemText
                      primary={event.title}
                      secondary={`Hora: ${new Date(event.start).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}`}
                    />
                    <Chip
                      size="small"
                      label={spanishStatus(event.status)}
                      color={getStatusColor(event.status ?? '')}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      )}
    </Drawer>
  );
};

export default DrawerSidebar;